// Block-based post editor: text, images, and video — all stored on R2.
// Each block type is a card; add/reorder/delete inline. Uploads show progress.

import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import type { Block } from "../lib/types";
import { uploadVideoThumbnail } from "../lib/videoThumbnail";

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

// ── Internal editor-block state ──────────────────────────────────────────────

type TextEditorBlock = {
  _id: string;
  type: "text";
  text: string;
};

type ImageEditorBlock =
  | { _id: string; type: "image"; settled: true; url: string }
  | { _id: string; type: "image"; settled: false; preview: string; progress: number; error?: string };

type VideoEditorBlock =
  // settled: R2 clip has a `url`; a legacy Stream clip has `streamUid` + `thumbnailUrl`.
  | { _id: string; type: "video"; settled: true; url: string | null; streamUid: string | null; thumbnailUrl: string | null }
  | { _id: string; type: "video"; settled: false; name: string; progress: number; error?: string };

type EditorBlock = TextEditorBlock | ImageEditorBlock | VideoEditorBlock;

function toCleanBlocks(eds: EditorBlock[]): Block[] {
  return eds
    .filter((b): b is TextEditorBlock | (ImageEditorBlock & { settled: true }) | (VideoEditorBlock & { settled: true }) => {
      if (b.type === "text") return true;
      return (b as { settled: boolean }).settled === true;
    })
    .map((b) => {
      if (b.type === "text") return { type: "text", text: b.text, url: null, streamUid: null, thumbnailUrl: null };
      if (b.type === "image") return { type: "image", text: null, url: (b as { url: string }).url, streamUid: null, thumbnailUrl: null };
      const vb = b as VideoEditorBlock & { settled: true };
      return { type: "video", text: null, url: vb.url, streamUid: vb.streamUid, thumbnailUrl: vb.thumbnailUrl };
    });
}

function fromBlock(b: Block): EditorBlock {
  const _id = uid();
  if (b.type === "text") return { _id, type: "text", text: b.text ?? "" };
  if (b.type === "image") return { _id, type: "image", settled: true, url: b.url ?? "" };
  return { _id, type: "video", settled: true, url: b.url ?? null, streamUid: b.streamUid ?? null, thumbnailUrl: b.thumbnailUrl ?? null };
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface BlockEditorProps {
  initialBlocks: Block[];
  onChange: (blocks: Block[]) => void;
  onPendingChange?: (hasPending: boolean) => void;
  disabled?: boolean;
}

// ── Main component ─────────────────────────────────────────────────────────────

export function BlockEditor({ initialBlocks, onChange, onPendingChange, disabled }: BlockEditorProps) {
  const [blocks, setBlocks] = useState<EditorBlock[]>(() => {
    if (initialBlocks.length === 0) return [{ _id: uid(), type: "text", text: "" }];
    return initialBlocks.map(fromBlock);
  });

  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const onPendingRef = useRef(onPendingChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onPendingRef.current = onPendingChange; }, [onPendingChange]);

  // Notify parent whenever block state settles
  useEffect(() => {
    const hasPending = blocks.some(
      (b) => b.type !== "text" && !(b as { settled?: boolean }).settled,
    );
    onPendingRef.current?.(hasPending);
    if (!hasPending) onChangeRef.current(toCleanBlocks(blocks));
  }, [blocks]);

  const updateBlock = useCallback((id: string, patch: Partial<EditorBlock>) => {
    setBlocks((prev) => prev.map((b) => (b._id === id ? { ...b, ...patch } as EditorBlock : b)));
  }, []);

  const deleteBlock = useCallback((id: string) => {
    setBlocks((prev) => {
      const next = prev.filter((b) => b._id !== id);
      return next.length === 0 ? [{ _id: uid(), type: "text", text: "" }] : next;
    });
  }, []);

  const moveBlock = useCallback((id: string, dir: -1 | 1) => {
    setBlocks((prev) => {
      const i = prev.findIndex((b) => b._id === id);
      if (i < 0) return prev;
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev] as EditorBlock[];
      const tmp = next[i]!;
      next[i] = next[j]!;
      next[j] = tmp;
      return next;
    });
  }, []);

  const addTextBlock = useCallback(() => {
    setBlocks((prev) => [...prev, { _id: uid(), type: "text", text: "" }]);
  }, []);

  async function handleImageFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const preview = URL.createObjectURL(file);
    const _id = uid();
    const pending: ImageEditorBlock = { _id, type: "image", settled: false, preview, progress: 0 };
    setBlocks((prev) => [...prev, pending]);

    try {
      const { url } = await api.uploadImage(file, (progress) => {
        setBlocks((prev) =>
          prev.map((b) => (b._id === _id ? { ...b, progress } as EditorBlock : b)),
        );
      });
      URL.revokeObjectURL(preview);
      setBlocks((prev) =>
        prev.map((b) => (b._id === _id ? ({ _id, type: "image", settled: true, url } as EditorBlock) : b)),
      );
    } catch (err) {
      URL.revokeObjectURL(preview);
      const msg = err instanceof ApiError ? err.message : "Upload failed — try again.";
      setBlocks((prev) =>
        prev.map((b) => (b._id === _id ? { ...b, error: msg } as EditorBlock : b)),
      );
    }
  }

  async function handleVideoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const _id = uid();
    const pending: VideoEditorBlock = { _id, type: "video", settled: false, name: file.name, progress: 0 };
    setBlocks((prev) => [...prev, pending]);

    try {
      const [thumbnailUrl, { url }] = await Promise.all([
        uploadVideoThumbnail(file),
        api.uploadVideo(file, (progress) => {
          setBlocks((prev) =>
            prev.map((b) => (b._id === _id ? { ...b, progress } as EditorBlock : b)),
          );
        }),
      ]);

      setBlocks((prev) =>
        prev.map((b) =>
          b._id === _id
            ? ({ _id, type: "video", settled: true, url, streamUid: null, thumbnailUrl } as EditorBlock)
            : b,
        ),
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Video upload failed — try again.";
      setBlocks((prev) =>
        prev.map((b) => (b._id === _id ? { ...b, error: msg } as EditorBlock : b)),
      );
    }
  }

  // Index of the first settled media block — it becomes the post cover
  const firstMediaIndex = blocks.findIndex(
    (b) => (b.type === "image" || b.type === "video") && (b as { settled?: boolean }).settled !== false,
  );

  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block, i) => (
        <BlockRow
          key={block._id}
          block={block}
          index={i}
          total={blocks.length}
          isCover={i === firstMediaIndex}
          disabled={disabled}
          onMoveUp={() => moveBlock(block._id, -1)}
          onMoveDown={() => moveBlock(block._id, 1)}
          onDelete={() => deleteBlock(block._id)}
          onTextChange={(text) => updateBlock(block._id, { text } as Partial<EditorBlock>)}
        />
      ))}

      {/* Hint about cover image */}
      {firstMediaIndex >= 0 && (
        <p className="px-1 text-xs text-ink-muted/70">
          📌 The first photo or video is your cover image on the public post.
        </p>
      )}

      {/* Add block buttons */}
      <div className="flex flex-wrap gap-2 pt-2">
        <AddBtn onClick={addTextBlock} disabled={disabled} icon="✏️" label="Add text" />
        <AddBtn onClick={() => imageInputRef.current?.click()} disabled={disabled} icon="📷" label="Add photo" />
        <AddBtn onClick={() => videoInputRef.current?.click()} disabled={disabled} icon="🎥" label="Add video" />
      </div>

      <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />
      <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={handleVideoFile} />
    </div>
  );
}

// ── Add button ─────────────────────────────────────────────────────────────────

function AddBtn({
  onClick,
  disabled,
  icon,
  label,
}: {
  onClick: () => void;
  disabled?: boolean;
  icon: string;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1.5 rounded-xl border-2 border-ink/30 bg-paper-deep px-4 py-2 text-sm font-semibold text-ink-soft shadow-[2px_2px_0_0_var(--color-ink)] transition-colors hover:border-ink hover:text-ink active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-40"
    >
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// ── Block row wrapper ───────────────────────────────────────────────────────────

interface BlockRowProps {
  block: EditorBlock;
  index: number;
  total: number;
  isCover: boolean;
  disabled?: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onTextChange: (text: string) => void;
}

function BlockRow({ block, index, total, isCover, disabled, onMoveUp, onMoveDown, onDelete, onTextChange }: BlockRowProps) {
  const canUp = index > 0;
  const canDown = index < total - 1;

  return (
    <div className="group relative flex gap-2">
      {/* Reorder controls — always faintly visible, full on hover/focus */}
      <div className="flex flex-col justify-center gap-0.5 opacity-40 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!canUp || disabled}
          aria-label="Move up"
          className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-paper-deep hover:text-ink disabled:opacity-20"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!canDown || disabled}
          aria-label="Move down"
          className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-paper-deep hover:text-ink disabled:opacity-20"
        >
          ↓
        </button>
      </div>

      {/* Block content */}
      <div className="min-w-0 flex-1">
        {block.type === "text" && (
          <TextBlock text={block.text} onChange={onTextChange} disabled={disabled} />
        )}
        {block.type === "image" && (
          <ImageBlock block={block} isCover={isCover} />
        )}
        {block.type === "video" && (
          <VideoBlock block={block} isCover={isCover} />
        )}
      </div>

      {/* Delete — always faintly visible on mobile, full on hover/focus */}
      <button
        type="button"
        onClick={onDelete}
        disabled={disabled}
        aria-label="Remove block"
        className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-ink-muted opacity-40 transition-all group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-love/10 hover:text-love disabled:pointer-events-none"
      >
        ×
      </button>
    </div>
  );
}

// ── Text block ─────────────────────────────────────────────────────────────────

function TextBlock({ text, onChange, disabled }: { text: string; onChange: (t: string) => void; disabled?: boolean }) {
  const ref = useRef<HTMLTextAreaElement>(null);

  function autoGrow() {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }

  useEffect(() => { autoGrow(); }, [text]);

  return (
    <textarea
      ref={ref}
      value={text}
      disabled={disabled}
      placeholder="Write something…"
      rows={3}
      onChange={(e) => { onChange(e.target.value); autoGrow(); }}
      className="w-full resize-none overflow-hidden rounded-xl border-2 border-ink/15 bg-paper-deep px-4 py-3 leading-relaxed text-ink placeholder:text-ink-muted/60 focus:border-citrus focus:bg-paper focus:outline-none disabled:opacity-50"
    />
  );
}

// ── Image block ────────────────────────────────────────────────────────────────

function ImageBlock({ block, isCover }: { block: ImageEditorBlock; isCover?: boolean }) {
  const [hasError, setHasError] = useState(false);

  if (!block.settled) {
    return (
      <div className="relative overflow-hidden rounded-xl border-2 border-ink/10 bg-paper-deep">
        <img
          src={block.preview}
          alt="Uploading…"
          className="h-48 w-full object-cover opacity-60 sm:h-64"
        />
        <div className="absolute inset-x-0 bottom-0 bg-black/40 px-4 py-3">
          {block.error ? (
            <p className="text-sm font-semibold text-love">{block.error}</p>
          ) : (
            <>
              <div className="mb-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/20">
                <div
                  className="h-full rounded-full bg-citrus transition-all duration-200"
                  style={{ width: `${block.progress}%` }}
                />
              </div>
              <p className="text-xs font-semibold text-white/80">Uploading… {block.progress}%</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-xl border-2 border-ink/10 bg-paper-deep">
      {isCover && !hasError && (
        <div className="absolute left-2 top-2 z-10 rounded-md bg-black/60 px-2 py-0.5 text-xs font-semibold text-white/90">
          Cover image
        </div>
      )}
      {hasError ? (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 p-6 text-center">
          <span className="text-4xl">🖼️</span>
          <p className="text-sm font-semibold text-ink-soft">Image couldn't load</p>
          <p className="max-w-xs break-all text-xs text-ink-muted">{block.url}</p>
        </div>
      ) : (
        <img
          src={block.url}
          alt=""
          className="block w-full"
          onError={() => setHasError(true)}
          loading="lazy"
        />
      )}
    </div>
  );
}

// ── Video block ────────────────────────────────────────────────────────────────

function VideoBlock({ block, isCover }: { block: VideoEditorBlock; isCover?: boolean }) {
  const [thumbError, setThumbError] = useState(false);

  if (!block.settled) {
    return (
      <div className="flex flex-col gap-3 rounded-xl border-2 border-ink/10 bg-paper-deep px-4 py-5">
        {block.error ? (
          <p className="text-sm font-semibold text-love">{block.error}</p>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <span className="text-2xl">🎥</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-ink-soft">{block.name}</p>
                <p className="text-xs text-ink-muted">Uploading…</p>
              </div>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink/10">
              <div
                className="h-full rounded-full bg-citrus transition-all duration-200"
                style={{ width: `${block.progress}%` }}
              />
            </div>
            <p className="text-xs text-ink-muted">{block.progress}%</p>
          </>
        )}
      </div>
    );
  }

  // R2-hosted clip → play inline with a native <video>.
  if (block.url) {
    return (
      <div className="overflow-hidden rounded-xl border-2 border-ink/10 bg-black">
        <div className="relative">
          {isCover && (
            <div className="absolute left-2 top-2 z-10 rounded-md bg-black/60 px-2 py-0.5 text-xs font-semibold text-white/90">
              Cover video
            </div>
          )}
          <video
            src={block.url}
            poster={block.thumbnailUrl ?? undefined}
            controls
            playsInline
            className="block max-h-[420px] w-full bg-black"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border-2 border-ink/10 bg-black">
      <div className="relative aspect-video w-full">
        {!thumbError && (
          <img
            src={block.thumbnailUrl ?? ""}
            alt="Video thumbnail"
            className="absolute inset-0 h-full w-full object-cover opacity-80"
            onError={() => setThumbError(true)}
          />
        )}
        {thumbError && (
          <div className="absolute inset-0 flex items-center justify-center bg-ink/80">
            <span className="text-4xl">🎥</span>
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/50 text-3xl backdrop-blur-sm">
            ▶
          </span>
        </div>
        {isCover && (
          <div className="absolute left-2 top-2 rounded-md bg-black/60 px-2 py-0.5 text-xs font-semibold text-white/90">
            Cover video
          </div>
        )}
        <div className="absolute bottom-2 left-3 rounded-md bg-black/60 px-2 py-0.5 text-xs font-semibold text-white/80">
          Video ready
        </div>
      </div>
    </div>
  );
}
